import { motion, useReducedMotion } from 'motion/react';
import { EASE_MK } from '@/Components/Marketing/easing';
import { useTone } from '@/Components/Marketing/Tone';
import { cn } from '@/lib/utils';

const LABEL = { light: 'text-mk-subtle', dark: 'text-mk-night-body' };
const DIVIDER = { light: 'md:border-mk-line', dark: 'md:border-white/10' };

// Light: muted grayscale until hovered, then full colour. `mix-blend-multiply`
// drops a white background into the page, so a PNG without transparency does
// not show as a box. Dark: the grayscale logo inverted and screened, so dark
// ink turns light and a white background turns black and disappears.
// Full-colour logos do not hold up on near-black, so dark only brightens on
// hover.
const LOGO = {
    light: 'opacity-70 grayscale hover:opacity-100 hover:grayscale-0',
    dark: 'opacity-70 grayscale invert hover:opacity-100',
};

// The blend sits on the logo's item, the element its entrance fades and
// moves: an element mid-fade (or moved) is a group of its own, and a logo
// blended inside it would blend with nothing and show its white box until
// the entrance ended.
const BLEND = { light: 'mix-blend-multiply', dark: 'mix-blend-screen' };

/**
 * How the logos come in (ask 2): once, the first time a third of the strip
 * is in view, each fades in and rises `rise` px over `duration` seconds on
 * the site's ease, `stagger` seconds after the one before it. On the logo's
 * item, so the logo's own resting opacity and hover stay its classes'.
 */
export const LOGO_ENTRANCE = Object.freeze({ rise: 8, duration: 0.5, ease: EASE_MK, stagger: 0.08, amount: 0.3 });

const ENTER = {
    hidden: { opacity: 0, y: LOGO_ENTRANCE.rise },
    shown: { opacity: 1, y: 0, transition: { duration: LOGO_ENTRANCE.duration, ease: LOGO_ENTRANCE.ease } },
};

/**
 * Partner logos in labelled groups ("Member of", "Trusted by"). Groups sit side
 * by side with a hairline between them from md up and stack on phones. Each
 * logo takes `src`, `alt`, `width`, `height` (intrinsic, for the aspect ratio)
 * and an optional `className` to adjust its rendered height optically. The
 * logos come in as LOGO_ENTRANCE says; under reduced motion they are simply
 * there.
 */
export default function LogoStrip({ groups, tone, className }) {
    const resolved = useTone(tone);
    const reduced = useReducedMotion();
    const Strip = reduced ? 'div' : motion.div;
    const Item = reduced ? 'li' : motion.li;
    const entrance = reduced ? {} : {
        initial: 'hidden',
        whileInView: 'shown',
        viewport: { once: true, amount: LOGO_ENTRANCE.amount },
        transition: { staggerChildren: LOGO_ENTRANCE.stagger },
    };

    return (
        <Strip className={cn('grid gap-10 md:grid-flow-col md:justify-start md:gap-0', className)} {...entrance}>
            {groups.map((group) => (
                <div
                    key={group.label}
                    className={cn(
                        'grid content-start gap-5 md:border-l md:px-10 md:first:border-l-0 md:first:pl-0 md:last:pr-0',
                        DIVIDER[resolved],
                    )}
                >
                    <p className={cn('text-sm', LABEL[resolved])}>{group.label}</p>
                    <ul role="list" className="flex flex-wrap items-center gap-x-10 gap-y-6">
                        {group.logos.map((logo) => (
                            <Item key={logo.src} className={BLEND[resolved]} {...(reduced ? {} : { variants: ENTER })}>
                                <img
                                    src={logo.src}
                                    alt={logo.alt}
                                    width={logo.width}
                                    height={logo.height}
                                    loading="lazy"
                                    decoding="async"
                                    className={cn(
                                        'h-10 w-auto transition duration-500 ease-mk motion-reduce:transition-none',
                                        LOGO[resolved],
                                        logo.className,
                                    )}
                                />
                            </Item>
                        ))}
                    </ul>
                </div>
            ))}
        </Strip>
    );
}
